import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesFooter from '../components/brand/SeriesFooter';
import { InsightGuidedInput, GatedInsightDisplay } from '../components/insight-hero';
import { Button } from '../components/ui/button';
import { 
  Loader2, 
  Brain, 
  Search, 
  Target, 
  TrendingUp, 
  ArrowRight,
  CheckCircle,
  Sparkles,
  HelpCircle,
  Lightbulb,
  Shield,
  Zap,
  Lock
} from 'lucide-react';

/**
 * HP.3: "Am I Actually Good at My Job?" Hero Page
 * 
 * Target: Professionals struggling with imposter syndrome
 * Hook: "See what you're actually good at — with evidence"
 * Maps to: Living CV capability domains
 * CTA: "List your recent projects"
 */

const InsightHeroPage = () => {
  const navigate = useNavigate();
  
  const [flowState, setFlowState] = useState('hero');
  const [inputData, setInputData] = useState(null);
  const [insightData, setInsightData] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Amber accent for insight/illumination theme
  const series = {
    name: 'Capability Insight',
    accentColor: '#f59e0b',
    slug: 'insight',
    author: { name: 'Paul Hooper' }
  };

  useEffect(() => {
    const pendingUnlock = sessionStorage.getItem('rv_pending_unlock');
    if (pendingUnlock) {
      const data = JSON.parse(pendingUnlock);
      if (data.flow_type === 'insight') {
        navigate(`/insight/complete?rv_id=${data.review_id}&hash=${data.input_hash}`);
      }
    }
  }, []);

  const handleStartInput = () => {
    setFlowState('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputComplete = async (data) => {
    setInputData(data);
    setFlowState('generating');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/review/generate-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: data.role,
          context: data.context,
          achievements: data.achievements,
          challenges: data.challenges || '',
          growth: data.growth || '',
          goals: '',
          flow_type: 'insight',
          optional_context: data.optional_paste || ''
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed');
      }

      const result = await response.json();
      setInsightData(result);
      setFlowState('result');
    } catch (err) {
      setError(err.message);
      setFlowState('input');
    }
  };

  const handleStartFresh = () => {
    setFlowState('input');
    setInsightData(null);
    setInputData(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setFlowState('hero');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ═══════════════════════════════════════════
  // PAIN POINTS
  // ═══════════════════════════════════════════
  const painPoints = [
    {
      icon: HelpCircle,
      title: "You dismiss your own wins",
      description: "You call your achievements 'just doing my job' while others take credit for less."
    },
    {
      icon: Search,
      title: "You can't articulate your strengths",
      description: "In interviews, you freeze. You know you're good but the words don't come."
    },
    {
      icon: Target,
      title: "You compare and come up short",
      description: "Everyone else seems more confident, more strategic, more... everything."
    },
    {
      icon: Shield,
      title: "You feel like you're faking it",
      description: "You wait for someone to realise you don't belong here. It hasn't happened yet."
    },
    {
      icon: Brain,
      title: "You can't see your own growth",
      description: "You focus on gaps instead of patterns. The progress is invisible to you."
    },
    {
      icon: Lightbulb,
      title: "You know you're capable — you just can't prove it",
      description: "Gut feeling says yes. But where's the evidence? Your inner critic wants receipts."
    },
  ];

  // ═══════════════════════════════════════════
  // HOW IT WORKS
  // ═══════════════════════════════════════════
  const howItWorks = [
    {
      step: 1,
      title: "Describe Your Recent Work",
      description: "List 2-3 projects, challenges you tackled, and outcomes. No need to polish it — raw is fine.",
      icon: Target,
    },
    {
      step: 2,
      title: "AI Maps Your Capabilities",
      description: "We analyse what you described and identify capability patterns, evidence chains, and hidden strengths.",
      icon: Brain,
    },
    {
      step: 3,
      title: "See Your Capability Profile",
      description: "A clear, evidence-backed picture of what you're actually good at. No vague claims — just proof.",
      icon: Sparkles,
    },
  ];

  // ═══════════════════════════════════════════
  // RENDER: GENERATING STATE
  // ═══════════════════════════════════════════
  if (flowState === 'generating') {
    return (
      <div className="insight-hero-page min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center" data-testid="insight-generating">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#f59e0b20' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#f59e0b' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Analysing your capabilities...</h2>
            <p className="text-gray-400 max-w-md mx-auto">
              Mapping your projects to capability domains and looking for evidence patterns. This takes about 15 seconds.
            </p>
          </div>
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // RENDER: INPUT STATE
  // ═══════════════════════════════════════════
  if (flowState === 'input') {
    return (
      <div className="insight-hero-page min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          {error && (
            <div data-testid="insight-error" className="mb-6 p-4 bg-red-900/20 border border-red-800/30 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          <InsightGuidedInput 
            onComplete={handleInputComplete}
            onCancel={handleCancel}
            accentColor="#f59e0b"
          />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // RENDER: RESULT STATE
  // ═══════════════════════════════════════════
  if (flowState === 'result') {
    return (
      <div className="insight-hero-page min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <GatedInsightDisplay 
            data={insightData}
            onStartFresh={handleStartFresh}
            accentColor="#f59e0b"
          />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // RENDER: HERO STATE (DEFAULT)
  // ═══════════════════════════════════════════
  return (
    <div data-testid="insight-hero-page" className="insight-hero-page min-h-screen bg-gray-950">
      <SeriesHeader series={series} />

      <main>
        {/* ─── HERO SECTION ─── */}
        <section data-testid="insight-hero-section" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-950/20 via-gray-950 to-gray-950" />
          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border" style={{ borderColor: '#f59e0b40', color: '#f59e0b', backgroundColor: '#f59e0b10' }}>
                <Brain className="w-3.5 h-3.5" />
                Capability Insight — Free Analysis
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                Am I Actually Good<br />
                <span style={{ color: '#f59e0b' }}>at My Job?</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-300 mb-4 max-w-2xl leading-relaxed">
                Imposter syndrome feeds on vague feelings. Let's replace them with evidence.
              </p>
              <p className="text-base text-gray-400 mb-10 max-w-2xl">
                Describe your recent work and we'll map your actual capabilities — backed by what you've done, not what you think.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="insight-start-btn"
                  onClick={handleStartInput}
                  className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                  style={{ backgroundColor: '#f59e0b' }}
                >
                  Map My Capabilities
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('insight-how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
                >
                  How does it work?
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                No signup required. Free instant analysis. Takes about 2 minutes.
              </p>
            </div>
          </div>
        </section>

        {/* ─── PAIN POINTS ─── */}
        <section data-testid="insight-pain-points" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Sound familiar?</h2>
              <p className="text-gray-400 max-w-lg mx-auto">
                Imposter syndrome isn't about confidence. It's about not having a clear view of your own capabilities.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {painPoints.map((point, i) => {
                const Icon = point.icon;
                return (
                  <div key={i} className="p-5 bg-gray-900/50 border border-gray-800/50 rounded-xl hover:border-gray-700 transition-colors">
                    <Icon className="w-5 h-5 mb-3" style={{ color: '#f59e0b' }} />
                    <h3 className="text-white font-semibold text-sm mb-1.5">{point.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{point.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section id="insight-how-it-works" data-testid="insight-how-it-works" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">How it works</h2>
              <p className="text-gray-400">Three steps. Two minutes. Evidence-backed clarity.</p>
            </div>
            <div className="space-y-6">
              {howItWorks.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="flex items-start gap-5 p-6 bg-gray-950/50 border border-gray-800/50 rounded-xl">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm" style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── DEMO OUTPUT ─── */}
        <section data-testid="insight-demo-output" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">What you'll get</h2>
              <p className="text-gray-400">A clear, evidence-backed profile of what you're actually good at.</p>
            </div>
            <div className="p-6 bg-gray-900/60 border border-gray-800 rounded-xl">
              {/* Sample visible capability */}
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-4 h-4" style={{ color: '#f59e0b' }} />
                <span className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Sample Capability Profile</span>
              </div>
              <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: '#f59e0b08', borderColor: '#f59e0b20', borderWidth: 1, borderStyle: 'solid' }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Based on your described work, you demonstrate strong capabilities in <strong className="text-white">technical problem-solving under pressure</strong>, 
                  <strong className="text-white"> cross-team coordination</strong>, and <strong className="text-white">mentoring</strong>. 
                  Your projects show a pattern of taking complex, ambiguous situations and creating clarity for others.
                </p>
              </div>
              <div className="space-y-2 mb-4">
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    <span className="text-white text-sm font-medium">Technical Problem-Solving Under Pressure</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5 ml-6">Evidence: Production incident response, architecture decisions under deadline...</p>
                </div>
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    <span className="text-white text-sm font-medium">Cross-Team Coordination</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5 ml-6">Evidence: Multi-team project delivery, stakeholder alignment...</p>
                </div>
              </div>
              {/* Gated preview */}
              <div className="relative p-4 rounded-lg border border-dashed border-gray-700 overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-gray-900/70 z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: '#f59e0b' }} />
                    <p className="text-gray-300 text-xs font-medium">3 more capabilities + blind spot analysis</p>
                  </div>
                </div>
                <div className="opacity-20 select-none">
                  <div className="h-4 bg-gray-700/50 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full mb-3" />
                  <div className="h-4 bg-gray-700/50 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-5/6" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section data-testid="insight-final-cta" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Stop guessing. Start knowing.
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Two minutes of input. An evidence-based capability profile. 
              Replace imposter syndrome with clarity.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-2xl mx-auto">
              {[
                { icon: CheckCircle, text: "Free — no card required" },
                { icon: Zap, text: "AI-powered analysis" },
                { icon: Shield, text: "Nothing stored without signup" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center justify-center gap-2 text-sm text-gray-300">
                    <Icon className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    {item.text}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="insight-final-cta-btn"
                onClick={handleStartInput}
                className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                style={{ backgroundColor: '#f59e0b' }}
              >
                Map My Capabilities
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/review')}
                className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
              >
                Try Performance Review instead
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SeriesFooter series={series} />
    </div>
  );
};

export default InsightHeroPage;
